<template>
  <div>
    <v-card-text>
      <div class="d-flex mb-2">
        <v-avatar>
          <img
            :src="reportableAuthorAvatar"
            :alt="reportableAuthorDisplayName"
          />
        </v-avatar>
        <div class="ml-3">
          <label class="d-block body-2">
            {{ reportableAuthorDisplayName }}
          </label>
          <label class="d-block caption grey--text"
            >{{ reportType }}ed
            {{ reportableAnonymousAthour ? 'anonymously' : '' }} on
            {{ report.postAddedDate }}
          </label>
        </div>
      </div>

      <div class="body-1 my-3">{{ reportableBody }}</div>

      <div v-if="reportableAttachment">
        <a class="caption" @click="showAttachment = !showAttachment"
          >Show / Hide Attachment Preview</a
        >
      </div>

      <div class="text-center" v-if="showAttachment">
        <video
          width="100%"
          height="300px"
          contain
          preload="metadata"
          controls
          :poster="reportableAttachment.thumb_url"
          v-if="reportableAttachment.mime_type.includes('video')"
        >
          <source
            :src="reportableAttachment.url"
            :type="reportableAttachment.mime_type"
          />
        </video>
        <v-card
          elevation="2"
          color="secondary"
          v-else-if="reportableAttachment.mime_type.includes('image')"
        >
          <v-img
            :src="reportableAttachment.url"
            :lazy-src="reportableAttachment.url"
            contain
            height="300px"
          ></v-img>
        </v-card>
      </div>

      <v-chip
        small
        class="mt-5 mr-2"
        v-for="tag in reportableTags"
        :key="tag.id"
        >{{ tag.tag_name }}</v-chip
      >

      <div class="text-right mt-2" v-if="reportableDeletedAt === null">
        <v-btn
          class="mr-3"
          small
          color="primary"
          @click="keepDialog = true"
          v-if="!isReportKept"
          >Keep</v-btn
        >
        <v-btn
          small
          color="error"
          v-if="!isReportKept"
          @click="deleteDialog = true"
          >Delete</v-btn
        >
      </div>
    </v-card-text>

    <v-dialog v-model="keepDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Confirm Keep</v-card-title>

        <v-card-text
          >Are you sure you want to KEEP the reported content? This cannot be
          undone.</v-card-text
        >

        <v-card-actions>
          <v-btn text @click="keepDialog = false">Cancel</v-btn>

          <v-spacer></v-spacer>

          <v-btn color="red" text @click="keepReport">Yes, Proceed</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>

        <v-card-text
          >Are you sure you want to DELETE the reported content? This cannot be
          undone.</v-card-text
        >

        <v-card-actions>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>

          <v-spacer></v-spacer>

          <v-btn color="red" text @click="deleteReportedContent"
            >Yes, Proceed</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import get from 'lodash/get'
import { mapActions } from 'vuex'

export default {
  data() {
    return {
      loading: false,
      deleteDialog: false,
      keepDialog: false,
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showAttachment: false,
      isReportKept: 0,
    }
  },

  props: ['report'],

  computed: {
    reportId() {
      return get(this.report, 'id')
    },
    reportableId() {
      return get(this.report, 'reportable.id')
    },
    reportableAuthorAvatar() {
      return get(this.report, 'reportable.author.avatar_permanent_url')
    },
    reportableAuthorDisplayName() {
      return get(this.report, 'reportable.author.display_name')
    },
    reportableAnonymousAthour() {
      return get(this.report, 'reportable.anonymous_author')
    },
    reportableBody() {
      return get(this.report, 'reportable.body')
    },
    reportableAttachment() {
      return get(this.report, 'reportable.attachment')
    },
    reportableTags() {
      return get(this.report, 'reportable.tags')
    },
    reportableDeletedAt() {
      return get(this.report, 'reportable.deleted_at')
    },
    reportType() {
      return get(this.report, 'report_type') === 'posts' ? 'Post' : 'Comment'
    },
  },

  mounted() {
    this.isReportKept = get(this.report, 'is_denied')
  },

  methods: {
    ...mapActions({
      deleteReportedPost: 'report/deleteReportedPost',
      deleteReportedComment: 'report/deleteReportedComment',
      keepReportedContent: 'report/keepReportedContent',
    }),

    keepReport() {
      this.keepReportedContent({
        reportId: this.reportId,
      }).then(() => {
        this.showSnackbar('Reported content is kept successfully!')
        this.isReportKept = true
      })
      this.loading = false
      this.keepDialog = false
    },

    deleteReportedContent() {
      this.loading = true
      if (this.reportType === 'Post') {
        this.deleteReportedPost({
          reportId: this.reportId,
          reportableId: this.reportableId,
        })
          .then(() => {
            this.loading = false
            this.deleteDialog = false
            this.showSnackbar('Post deleted successfully!')
          })
          .catch((err) => {
            this.loading = false
            if (err.response.status === 404) {
              this.deleteDialog = false
              this.showSnackbar(
                'The post you are trying to delete is no longer existing.',
                'error'
              )
            }
          })
      } else {
        this.deleteReportedComment({
          reportId: this.reportId,
          reportableId: this.reportableId,
        })
          .then(() => {
            this.loading = false
            this.deleteDialog = false
            this.showSnackbar('Comment deleted successfully!')
          })
          .catch((err) => {
            this.loading = false
            if (err.response.status === 404) {
              this.deleteDialog = false
              this.showSnackbar(
                'The comment you are trying to delete is no longer existing.',
                'error'
              )
            }
          })
      }
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },
}
</script>
